import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import webSocketService from '@/services/websocket.service';

// Ephemeral comment/edit-drawer presence, keyed by an opaque target
// string: "li:<lineItemId>" for a line item, "quote:<hash>" (etc.) for a
// record-level comment form. Fully separate from record_change: NEVER writes
// the DB, adds highlights, invalidates queries, or calls markSelfMutation. A
// module singleton external store fed by ONE app-wide WS subscription + ONE
// sweep timer; components read their target's typists via useSyncExternalStore.
// Only targets with active typists re-render (empty state returns a shared
// frozen array reference).

const EMPTY = Object.freeze([]);
const TYPING_TTL_MS = 6000; // 2× the 3s heartbeat: one missed beat still shows
// Background tabs can run timers only once a minute. Explicit close/disconnect
// events still remove editing presence immediately; this only covers lost peers.
const EDITING_TTL_MS = 90000;

const byTarget = new Map(); // target -> Map<user/session, {userId, username, expiresAt}>
const broadcasts = new Map(); // target -> shared broadcast while any local form is open
const listeners = new Set();
const snapshotCache = new Map(); // target -> stable typists array (per version)

const emit = () => {
  snapshotCache.clear();
  listeners.forEach((l) => l());
};

export const applyTypingEvent = ({ target, user_id, username, composing, editing, session_id }) => {
  if (typeof target !== 'string' || !target || user_id == null) return;
  const source = JSON.stringify([String(user_id), session_id || 'legacy']);
  const users = byTarget.get(target);
  if (composing === false) {
    if (users && users.delete(source)) {
      if (!users.size) byTarget.delete(target);
      emit();
    }
    return;
  }
  const map = users || new Map();
  if (!users) byTarget.set(target, map);
  map.set(source, {
    userId: user_id,
    username: username || 'Cineva',
    expiresAt: Date.now() + (editing ? EDITING_TTL_MS : TYPING_TTL_MS),
  });
  emit();
};

const sweep = () => {
  const now = Date.now();
  let changed = false;
  for (const [target, users] of byTarget) {
    for (const [uid, info] of users) {
      if (info.expiresAt < now) {
        users.delete(uid);
        changed = true;
      }
    }
    if (!users.size) byTarget.delete(target);
  }
  if (changed) emit();
};

const subscribe = (cb) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};

// Stable array of usernames typing on this target. Cached per emit() so
// useSyncExternalStore sees a stable reference and doesn't loop.
//
// The server excludes the sending socket. Every received session belongs to
// another tab, even if it uses the same account. Opening our own form must not
// hide those sessions or change the indicator beside it.
const getTypists = (target) => {
  if (snapshotCache.has(target)) return snapshotCache.get(target);
  const users = byTarget.get(target);
  let result = EMPTY;
  if (users && users.size) {
    const names = new Map();
    for (const info of users.values()) {
      names.set(String(info.userId), info.username);
    }
    result = names.size ? [...names.values()] : EMPTY;
  }
  snapshotCache.set(target, result);
  return result;
};

// Components read who is typing on their target. Returns a stable [] when
// nobody is, so idle rows never re-render on unrelated typing events.
export const useTypists = (target) =>
  useSyncExternalStore(
    subscribe,
    () => (target ? getTypists(target) : EMPTY),
    () => EMPTY,
  );

const recordPresenceTargets = (kind, recordId, lineItems) => {
  if (!recordId) return [];
  const targets = [recordTarget(kind, recordId)];
  for (const entry of lineItems || []) {
    const id = Number((entry?.line_item || entry)?.id);
    if (Number.isSafeInteger(id) && id > 0) targets.push(lineItemTarget(id));
  }
  return [...new Set(targets)].sort();
};

// A comment on one item also reserves the containing record's edit controls.
export const useRecordTypists = (kind, recordId, lineItems) => {
  const targetsKey = JSON.stringify(recordPresenceTargets(kind, recordId, lineItems));
  return useSyncExternalStore(subscribe, () => {
    const key = `record-presence:${targetsKey}`;
    if (snapshotCache.has(key)) return snapshotCache.get(key);
    const names = new Map();
    for (const target of JSON.parse(targetsKey)) {
      for (const info of byTarget.get(target)?.values() || []) names.set(String(info.userId), info.username);
    }
    const result = names.size ? [...names.values()] : EMPTY;
    snapshotCache.set(key, result);
    return result;
  }, () => EMPTY);
};

// A comment and one or more drawers can cover the same target in this tab.
// Only the last owner sends "stopped"; all owners share one heartbeat.
const HEARTBEAT_MS = 3000;
const acquireBroadcast = (target, editing = false) => {
  let broadcast = broadcasts.get(target);
  if (!broadcast) {
    const announce = () => webSocketService.sendTyping({
      target, composing: true, ...(broadcast.editors > 0 ? { editing: true } : {}),
    });
    broadcast = {
      owners: 0,
      editors: 0,
      announce,
      timer: setInterval(announce, HEARTBEAT_MS),
      unsubscribe: webSocketService.addReconnectListener(announce),
    };
    broadcasts.set(target, broadcast);
  }
  const wasEditing = broadcast.editors > 0;
  broadcast.owners += 1;
  if (editing) broadcast.editors += 1;
  if (broadcast.owners === 1 || wasEditing !== (broadcast.editors > 0)) broadcast.announce();

  return () => {
    broadcast.owners -= 1;
    if (editing) broadcast.editors -= 1;
    if (broadcast.owners === 0) {
      clearInterval(broadcast.timer);
      broadcast.unsubscribe();
      webSocketService.sendTyping({ target, composing: false });
      broadcasts.delete(target);
    } else if (editing && broadcast.editors === 0) {
      broadcast.announce();
    }
  };
};

// Comment forms keep their existing onComposingChange API. Capturing the target
// in the effect also stops the old target if the component switches records.
export const useTypingBroadcast = (target) => {
  const [composing, setComposing] = useState(false);
  useEffect(() => {
    if (target && composing) return acquireBroadcast(target);
  }, [target, composing]);
  return setComposing;
};

// Wire this inside the shared editor, so every entry point has the same
// lifecycle. Use persisted item IDs, including the nested sale item shape.
export const useRecordEditingPresence = ({ isOpen, kind, recordId, lineItems }) => {
  const targetKey = JSON.stringify(isOpen ? recordPresenceTargets(kind, recordId, lineItems) : []);
  const releases = useRef(new Map());

  useEffect(() => {
    const nextTargets = new Set(JSON.parse(targetKey));
    for (const [target, release] of releases.current) {
      if (!nextTargets.has(target)) {
        release();
        releases.current.delete(target);
      }
    }
    for (const target of nextTargets) {
      if (!releases.current.has(target)) releases.current.set(target, acquireBroadcast(target, true));
    }
  }, [targetKey]);

  useEffect(() => {
    const active = releases.current;
    return () => {
      active.forEach(release => release());
      active.clear();
    };
  }, []);
};

// Mounted ONCE app-wide (next to useRecordChangeListInvalidation): the single
// place receiving record_typing events. Feeds the store and runs the expiry
// sweep; sender exclusion happens on the server, per connection.
export const useTypingPresence = () => {
  useEffect(() => {
    const unsubscribe = webSocketService.addRecordTypingListener(applyTypingEvent);
    const sweepTimer = setInterval(sweep, HEARTBEAT_MS);
    return () => {
      unsubscribe();
      clearInterval(sweepTimer);
    };
  }, []);
};

// Target builders — keep the namespace consistent across call sites.
export const lineItemTarget = (lineItemId) => `li:${lineItemId}`;
export const recordTarget = (kind, ref) => `${kind}:${ref}`;
